const COPY = {
  en: {
    eyebrow: "AMYPARFUME DATABASE",
    title: "Find your perfume",
    reset: "Clear all",
    searchLabel: "Search",
    searchPlaceholder: "Name, brand, note or code",
    brand: "Brand",
    family: "Family",
    origin: "Origin",
    note: "Note",
    volume: "Volume",
    price: "Price up to, RSD",
    anyPrice: "Any",
    sort: "Sort",
    recommended: "Recommended",
    priceAsc: "Price: low to high",
    priceDesc: "Price: high to low",
    titleSort: "Name",
    inStock: "In stock only",
    all: "All",
    loading: "Loading perfume database…",
    results: (shown, total) => `${shown} of ${total} perfumes`,
    noResults: "No perfumes match these filters.",
    unavailable: "The extended search is temporarily unavailable. The full catalog remains below.",
    incomplete: (count) => `${count} Shopify cards could not be matched to the database.`,
  },
  ru: {
    eyebrow: "БАЗА AMYPARFUME",
    title: "Найдите свой аромат",
    reset: "Сбросить всё",
    searchLabel: "Поиск",
    searchPlaceholder: "Название, бренд, нота или код",
    brand: "Бренд",
    family: "Семейство",
    origin: "Страна",
    note: "Нота",
    volume: "Объём",
    price: "Цена до, RSD",
    anyPrice: "Любая",
    sort: "Сортировка",
    recommended: "Рекомендуемые",
    priceAsc: "Цена: по возрастанию",
    priceDesc: "Цена: по убыванию",
    titleSort: "По названию",
    inStock: "Только в наличии",
    all: "Все",
    loading: "Загружаем базу ароматов…",
    results: (shown, total) => `${shown} из ${total} ароматов`,
    noResults: "По этим условиям ароматов не найдено.",
    unavailable: "Расширенный поиск временно недоступен. Полный каталог остаётся ниже.",
    incomplete: (count) => `${count} карточек Shopify не удалось сопоставить с базой.`,
  },
  sr: {
    eyebrow: "AMYPARFUME BAZA",
    title: "Pronađite svoj parfem",
    reset: "Obriši sve",
    searchLabel: "Pretraga",
    searchPlaceholder: "Naziv, brend, nota ili šifra",
    brand: "Brend",
    family: "Mirisna porodica",
    origin: "Poreklo",
    note: "Nota",
    volume: "Zapremina",
    price: "Cena do, RSD",
    anyPrice: "Bilo koja",
    sort: "Sortiranje",
    recommended: "Preporučeno",
    priceAsc: "Cena: od niže ka višoj",
    priceDesc: "Cena: od više ka nižoj",
    titleSort: "Po nazivu",
    inStock: "Samo na stanju",
    all: "Sve",
    loading: "Učitavamo bazu parfema…",
    results: (shown, total) => `${shown} od ${total} parfema`,
    noResults: "Nema parfema za izabrane uslove.",
    unavailable: "Proširena pretraga trenutno nije dostupna. Ceo katalog je i dalje ispod.",
    incomplete: (count) => `${count} Shopify kartica nije povezano sa bazom.`,
  },
};

const NEW_PERFUME_SEARCH_ALIASES = [
  "new",
  "newest",
  "new arrival",
  "new arrivals",
  "novelty",
  "novelties",
  "новая",
  "новое",
  "новые",
  "новый",
  "новинка",
  "новинки",
  "nov",
  "nova",
  "novo",
  "novi",
  "novitet",
  "noviteti",
].join(" ");

const NEW_PERFUME_SEARCH_QUERIES = new Set([
  "new",
  "new perfume",
  "new perfumes",
  "new arrival",
  "new arrivals",
  "novelty",
  "novelties",
  "новая парфюмерия",
  "новые ароматы",
  "новые духи",
  "новинка",
  "новинки",
  "nov parfem",
  "novi parfemi",
  "novo",
  "novitet",
  "noviteti",
]);

function normalizedLocale(value) {
  const locale = String(value || "").toLowerCase().split("-")[0];
  return Object.hasOwn(COPY, locale) ? locale : "en";
}

export function normalizeSearch(value, locale = "en") {
  return String(value || "")
    .toLocaleLowerCase(locale)
    .replace(/[ё]/g, "е")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

function localizedValue(product, field, locale) {
  const fallbacks = locale === "sr" ? ["sr", "en", "ru"] : locale === "en" ? ["en", "ru"] : ["ru", "en"];
  for (const language of fallbacks) {
    const value = product.translations?.[language]?.[field];
    if (String(value || "").trim()) return String(value).trim();
  }
  return String(product[field] || "").trim();
}

function noteTokens(product, locale) {
  const localized = localizedValue(product, "notes", locale)
    .split(/[;,·/\n]+/)
    .map((value) => value.trim().replace(/^[\s.:—–-]+|[\s.:—–-]+$/g, ""))
    .filter(Boolean);
  return [...new Set(localized.length ? localized : (product.note_tokens || []))];
}

function searchableText(product, locale) {
  const translations = Object.values(product.translations || {}).flatMap((translation) => [
    translation.title,
    translation.brand,
    translation.family,
    translation.origin,
    translation.notes,
    translation.evolution,
    translation.description,
  ]);
  return normalizeSearch([
    product.title,
    product.brand,
    product.family,
    product.origin,
    product.notes,
    product.evolution,
    product.description,
    product.csv_code,
    product.ainur_code,
    ...(product.note_tokens || []),
    ...(product.variants || []).flatMap((variant) => [variant.label, variant.sku]),
    ...translations,
  ].join(" "), locale);
}

function productView(product, locale) {
  return {
    product,
    title: localizedValue(product, "title", locale),
    brand: localizedValue(product, "brand", locale),
    family: localizedValue(product, "family", locale),
    origin: localizedValue(product, "origin", locale),
    notes: noteTokens(product, locale),
    volumes: (product.variants || [])
      .filter((variant) => variant.type === "decant" || variant.type === "full")
      .map((variant) => String(variant.label || "").trim())
      .filter(Boolean),
    minPrice: Number(product.min_price || 0),
    inStock: Boolean(product.in_stock),
    searchable: searchableText(product, locale),
  };
}

function score(view, tokens, locale) {
  const title = normalizeSearch(view.title, locale);
  const brand = normalizeSearch(view.brand, locale);
  return tokens.reduce((total, token) => {
    if (title.includes(token)) return total + 5;
    if (brand.includes(token)) return total + 4;
    if (view.searchable.includes(token)) return total + 1;
    return total;
  }, 0) + Number(view.inStock);
}

function uniqueSorted(values, locale) {
  const unique = new Map();
  for (const value of values) {
    const text = String(value || "").trim();
    const key = normalizeSearch(text, locale);
    if (key && !unique.has(key)) unique.set(key, text);
  }
  return [...unique.values()].sort((a, b) => a.localeCompare(b, locale));
}

class AmyPerfumeSearch extends HTMLElement {
  constructor() {
    super();
    this.locale = normalizedLocale(this.dataset.locale || document.documentElement.lang);
    this.copy = COPY[this.locale];
    this.records = [];
    this.controls = {};
  }

  connectedCallback() {
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";
    this.dataset.ready = "false";
    this.translateUi();
    this.captureControls();
    this.load();
  }

  translateUi() {
    for (const node of this.querySelectorAll("[data-amy-i18n]")) {
      const value = this.copy[node.dataset.amyI18n];
      if (typeof value === "string") node.textContent = value;
    }
    for (const node of this.querySelectorAll("[data-amy-i18n-placeholder]")) {
      node.placeholder = this.copy[node.dataset.amyI18nPlaceholder] || "";
    }
    for (const node of this.querySelectorAll("[data-amy-i18n-aria]")) {
      node.setAttribute("aria-label", this.copy[node.dataset.amyI18nAria] || "");
    }
  }

  captureControls() {
    for (const role of ["query", "brand", "family", "origin", "note", "volume", "price", "sort", "stock", "status", "error", "reset"]) {
      this.controls[role] = this.querySelector(`[data-amy-role="${role}"]`);
    }
    for (const role of ["query", "brand", "family", "origin", "note", "volume", "price", "sort", "stock"]) {
      this.controls[role]?.addEventListener("input", () => this.apply());
      this.controls[role]?.addEventListener("change", () => this.apply());
    }
    this.controls.reset?.addEventListener("click", () => this.reset());
  }

  async load() {
    try {
      const response = await fetch(this.dataset.catalogUrl, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`catalog ${response.status}`);
      const catalog = await response.json();
      const products = (catalog.products || []).filter((product) => product.department === "perfumes");
      const byCode = new Map(products.map((product) => [String(product.ainur_code || "").padStart(5, "0"), product]));
      const byHandle = new Map(products.map((product) => [String(product.handle || ""), product]));
      const scope = this.closest("results-list") || document;
      const cards = [...scope.querySelectorAll(".product-grid__item[data-product-handle]")];
      let missing = 0;

      this.records = cards.map((card, index) => {
        const code = String(card.dataset.ainurCode || "").padStart(5, "0");
        const product = byCode.get(code) || byHandle.get(card.dataset.productHandle);
        if (!product) {
          missing += 1;
          return null;
        }
        const view = productView(product, this.locale);
        if (card.dataset.amyNewPerfume === "true") {
          view.searchable = `${view.searchable} ${NEW_PERFUME_SEARCH_ALIASES}`;
        }
        return { card, index, view };
      }).filter(Boolean);

      if (!this.records.length) throw new Error("no matching perfume cards");
      this.grid = this.records[0].card.parentElement;
      this.populateFilters();
      this.restoreUrlState();
      this.dataset.ready = "true";
      if (missing) {
        this.controls.error.hidden = false;
        this.controls.error.textContent = this.copy.incomplete(missing);
      }
      this.apply({ updateUrl: false });
    } catch (error) {
      this.dataset.ready = "false";
      this.controls.status.textContent = this.copy.unavailable;
      this.controls.error.hidden = false;
      this.controls.error.textContent = String(error?.message || error);
    }
  }

  populateFilters() {
    this.setOptions("brand", this.records.map(({ view }) => view.brand));
    this.setOptions("family", this.records.map(({ view }) => view.family));
    this.setOptions("origin", this.records.map(({ view }) => view.origin));
    this.setOptions("note", this.records.flatMap(({ view }) => view.notes).filter((note) => note.length <= 48));
    this.setOptions("volume", this.records.flatMap(({ view }) => view.volumes), (a, b) => (
      (parseInt(a, 10) || 9999) - (parseInt(b, 10) || 9999) || a.localeCompare(b, this.locale)
    ));
  }

  setOptions(role, values, sorter = null) {
    const select = this.controls[role];
    const options = uniqueSorted(values, this.locale);
    if (sorter) options.sort(sorter);
    select.replaceChildren(new Option(this.copy.all, ""));
    for (const value of options) select.add(new Option(value, value));
  }

  restoreUrlState() {
    const params = new URLSearchParams(window.location.search);
    const mapping = {
      query: "amy_q",
      brand: "amy_brand",
      family: "amy_family",
      origin: "amy_origin",
      note: "amy_note",
      volume: "amy_volume",
      price: "amy_price",
      sort: "amy_sort",
    };
    for (const [role, key] of Object.entries(mapping)) {
      const value = params.get(key);
      if (value !== null && this.controls[role]) this.controls[role].value = value;
    }
    this.controls.stock.checked = params.get("amy_stock") === "1";
  }

  updateUrl() {
    const url = new URL(window.location.href);
    const mapping = {
      query: "amy_q",
      brand: "amy_brand",
      family: "amy_family",
      origin: "amy_origin",
      note: "amy_note",
      volume: "amy_volume",
      price: "amy_price",
      sort: "amy_sort",
    };
    for (const [role, key] of Object.entries(mapping)) {
      const value = String(this.controls[role]?.value || "").trim();
      if (value && !(role === "sort" && value === "recommended")) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    }
    if (this.controls.stock.checked) url.searchParams.set("amy_stock", "1");
    else url.searchParams.delete("amy_stock");
    history.replaceState({}, "", url);
  }

  apply({ updateUrl = true } = {}) {
    if (!this.records.length) return;
    const normalizedQuery = normalizeSearch(this.controls.query.value, this.locale);
    const tokens = normalizedQuery.split(" ").filter(Boolean);
    const newPerfumesOnly = NEW_PERFUME_SEARCH_QUERIES.has(normalizedQuery);
    const maxPrice = Number(this.controls.price.value || 0);
    const selectedNote = normalizeSearch(this.controls.note.value, this.locale);
    let visible = this.records.filter(({ card, view }) => {
      if (newPerfumesOnly && card.dataset.amyNewPerfume !== "true") return false;
      if (tokens.length && !tokens.every((token) => view.searchable.includes(token))) return false;
      if (this.controls.brand.value && view.brand !== this.controls.brand.value) return false;
      if (this.controls.family.value && view.family !== this.controls.family.value) return false;
      if (this.controls.origin.value && view.origin !== this.controls.origin.value) return false;
      if (selectedNote && !view.notes.some((note) => normalizeSearch(note, this.locale) === selectedNote)) return false;
      if (this.controls.volume.value && !view.volumes.includes(this.controls.volume.value)) return false;
      if (maxPrice > 0 && view.minPrice > maxPrice) return false;
      if (this.controls.stock.checked && !view.inStock) return false;
      return true;
    });

    if (tokens.length) {
      visible.sort((a, b) => score(b.view, tokens, this.locale) - score(a.view, tokens, this.locale) || a.index - b.index);
    } else if (this.controls.sort.value === "price-asc") {
      visible.sort((a, b) => a.view.minPrice - b.view.minPrice || a.index - b.index);
    } else if (this.controls.sort.value === "price-desc") {
      visible.sort((a, b) => b.view.minPrice - a.view.minPrice || a.index - b.index);
    } else if (this.controls.sort.value === "title") {
      visible.sort((a, b) => a.view.title.localeCompare(b.view.title, this.locale));
    } else {
      visible.sort((a, b) => Number(b.view.inStock) - Number(a.view.inStock) || a.view.title.localeCompare(b.view.title, this.locale));
    }

    const visibleCards = new Set(visible.map(({ card }) => card));
    for (const { card } of this.records) card.hidden = !visibleCards.has(card);
    for (const { card } of visible) this.grid.append(card);
    this.controls.status.textContent = visible.length
      ? this.copy.results(visible.length, this.records.length)
      : this.copy.noResults;
    if (updateUrl) this.updateUrl();
  }

  reset() {
    for (const role of ["query", "brand", "family", "origin", "note", "volume", "price"]) {
      this.controls[role].value = "";
    }
    this.controls.sort.value = "recommended";
    this.controls.stock.checked = false;
    this.apply();
    this.controls.query.focus();
  }
}

if (!customElements.get("amy-perfume-search")) {
  customElements.define("amy-perfume-search", AmyPerfumeSearch);
}
